import { useTranslation } from "react-i18next";

import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { notifySuccess } from "@/lib/notify";
import { useApiClient } from "@/api/client-context";
import { toModifiedProcessModelId } from "@/api/identifiers";

export type SaveModelFileInput = {
  readonly processModelId: string;
  readonly fileName: string;
  readonly contents: string;
  /**
   * 読み込んだ時点の内容のハッシュ。backend はこれが現在の内容と一致しなければ
   * 409 を返す（他の人の変更を黙って上書きしないため）。null で送ると
   * backend は競合チェックをしない（図の取り込みはこれで主 BPMN を上書きする）。
   */
  readonly contentsHash: string | null;
};

/**
 * モデル内の 1 ファイルを保存する。
 *
 * backend は multipart の `file` フィールドを読み、**保存先はそのパートの
 * ファイル名で決まる**（URL のパスだけでは決まらない）ので必ず名前を付ける。
 */
export async function saveModelFile(
  client: ApiClient,
  input: SaveModelFileInput,
): Promise<void> {
  const modifiedId = toModifiedProcessModelId(input.processModelId);
  const body = new FormData();
  body.append(
    "file",
    new File([input.contents], input.fileName, { type: "application/xml" }),
  );

  await client.put(
    `/process-models/${modifiedId}/files/${input.fileName}`,
    body,
    { query: { file_contents_hash: input.contentsHash } },
  );
}

export function useSaveModelFile(): UseMutationResult<
  void,
  Error,
  SaveModelFileInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");

  return useMutation({
    mutationFn: (input: SaveModelFileInput) => saveModelFile(client, input),
    onSuccess: async () => {
      // 画面が変わらないので通ったことを知らせる（押し直すと 409 になる）
      notifySuccess(t("toast.saved"));
      // 保存後のハッシュを取り直さないと、次の保存が必ず 409 になる
      await queryClient.invalidateQueries({ queryKey: ["process-models"] });
    },
  });
}
